import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';

import useAnimate from '../ui/Motion';

import Reveal from '../ui/Reveal';
import WordReveal from '../ui/WordReveal';
import { contact, cta } from '../data/site';

/** The closing call to action that ends every subpage. */
export default function Cta({
    eyebrow = cta.eyebrow,
    title = cta.title,
    lead = cta.lead
}) {
    const ref = useRef(null);
    const reduced = !useAnimate();

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end end']
    });

    /* A rise rather than a scale: the glow is a 70px-blurred 1200px
       surface, and scaling it makes the compositor re-rasterise that blur
       every frame, which bands and drops tiles. Translating a composited
       layer is free. */
    const glowY = useTransform(scrollYProgress, [0, 1], ['10%', '0%']);
    const glowOpacity = useTransform(scrollYProgress, [0, 0.85], [0, 1]);

    return (
        <section className="cta" ref={ref}>
            {/* `x: '-50%'` has to be handed to framer, not left in CSS: framer
                builds the inline transform from the motion values it is given
                and that string replaces the stylesheet's `translateX(-50%)`
                wholesale, which would shunt the glow half its own width right
                and clip it against the section's `overflow: hidden`. */}
            <motion.div
                className="contact__glow"
                style={reduced ? undefined : { x: '-50%', y: glowY, opacity: glowOpacity }}
                aria-hidden="true"
            />

            <div className="shell cta__inner">
                <Reveal y={18}>
                    <span className="t-eyebrow t-eyebrow--accent">{eyebrow}</span>
                </Reveal>

                <WordReveal as="h2" className="t-display cta__title" text={title} />

                <Reveal delay={0.14}>
                    <p className="t-lead cta__lead">{lead}</p>
                </Reveal>

                <Reveal delay={0.2} className="cta__actions">
                    <a className="btn btn--accent" href={contact.phoneHref}>
                        Call {contact.phone}
                    </a>
                    <Link className="btn btn--ghost" to="/#contact">
                        Request a consultation
                        <span className="btn__arrow" aria-hidden="true">→</span>
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}
